import axios from 'axios'
import { toast } from 'react-toastify'
import { getCurrentApiUrl } from '../utils/backendConfig'
import { getAuthHeaders, clearStoredAuth } from './authTokenService'

// Create axios instance with dynamic base URL
const apiClient = axios.create()

// Set initial base URL
apiClient.defaults.baseURL = getCurrentApiUrl()

// Simple debouncing to prevent too many /auth/me calls
let lastAuthMeCall = 0
const MIN_AUTH_ME_INTERVAL = 5000 // Increased to 5 seconds minimum between calls to reduce API load

// Track the last pathname to detect route changes
let lastPathname = ''

// Track ongoing auth check to prevent multiple simultaneous calls
let authCheckInProgress = false

// Helper function to force logout
const forceLogout = () => {
  clearStoredAuth()
  
  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// Helper function to call /auth/me and check user status
const checkUserStatus = async () => {
  try {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress) {
      return true
    }
    
    // Simple debouncing - increased interval to prevent loops
    const now = Date.now()
    if (now - lastAuthMeCall < MIN_AUTH_ME_INTERVAL) {
      return true // Skip check if called too recently
    }
    
    authCheckInProgress = true
    lastAuthMeCall = now
    
    const tokenObj = localStorage.getItem('kt-auth-react-v')
    if (!tokenObj) {
      authCheckInProgress = false
      return false
    }
    
    const parsedTokenObj = JSON.parse(tokenObj)
    const token = parsedTokenObj.laravel_token ?? parsedTokenObj.token
    
    if (!token) {
      authCheckInProgress = false
      return false
    }
    
    // Call /auth/me directly with proper headers for current backend
    const authHeaders = getAuthHeaders()
    const response = await axios.get(`${getCurrentApiUrl()}/auth/me`, {
      headers: {
        ...authHeaders,
        'Accept': 'application/json',
      },
      timeout: 5000 // Add timeout to prevent hanging requests
    })
    
    const userData = response.data?.data || response.data?.user || response.data
    
    // Update stored auth data with fresh user data
    if (userData) {
      const updatedAuth = {
        ...parsedTokenObj,
        user: userData
      }
      localStorage.setItem('kt-auth-react-v', JSON.stringify(updatedAuth))
      
      // Trigger Redux state update by dispatching a custom event
      window.dispatchEvent(new CustomEvent('auth-user-updated', { detail: userData }))
    }
    
    // Check if user is disabled - handle both number and string status
    if (userData && (userData.status === 0 || userData.status === "0" || userData.status === false)) {
      authCheckInProgress = false
      forceLogout()
      return false
    }
    
    authCheckInProgress = false
    return true
  } catch (error) {
    authCheckInProgress = false
    // If 401 unauthorized, force logout
    if ((error as any).response?.status === 401) {
      forceLogout()
      return false
    }
    // For other errors, continue with the request
    return true
  }
}

// Function to handle route changes and trigger /auth/me
export const handleRouteChange = (newPathname: string) => {
  // Skip if pathname hasn't changed
  if (lastPathname === newPathname) {
    return
  }
  
  // Update last pathname
  lastPathname = newPathname
  
  // Skip auth routes and other excluded routes
  const skipAuthMeRoutes = ['/login', '/logout', '/auth/me', '/auth/register', '/forgot-password', '/reset-password', '/set-password', '/email-confirmation']
  const shouldSkipAuthMe = skipAuthMeRoutes.some(route => newPathname.includes(route))
  
  if (shouldSkipAuthMe) {
    return
  }
    
  // Call checkUserStatus asynchronously
  checkUserStatus().catch(error => {
    console.error('')
  })
}

// Add request interceptor to set dynamic base URL and authentication headers
apiClient.interceptors.request.use(
  async (config) => {
    // Set dynamic base URL for each request
    config.baseURL = getCurrentApiUrl()
    
    // Skip auth/me call for login, logout, and auth/me itself to avoid infinite loops
    const skipAuthMeEndpoints = ['/auth/login', '/auth/logout', '/auth/me', '/auth/register']
    const isSkipAuthMeEndpoint = skipAuthMeEndpoints.some(endpoint => config.url?.includes(endpoint))
    
    // Always call /auth/me before any API request (except for the excluded endpoints)
    if (!isSkipAuthMeEndpoint) {
      const isUserValid = await checkUserStatus()
      if (!isUserValid) {
        return Promise.reject(new Error('User account has been disabled or unauthorized'))
      }
    }
    
    // Set authentication headers based on current backend
    const authHeaders = getAuthHeaders()
    config.headers = {
      ...config.headers,
      ...authHeaders
    }
        
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const status = (error as any).response?.status

    // Handle unauthorized errors by forcing logout
    if (status === 401) {
      forceLogout()
    }

    // Handle rate limiting (429) gracefully with deduplicated toast
    if (status === 429) {
      toast.error('Too many requests. Please wait a moment and try again.', {
        toastId: 'rate-limit-error', // Prevents duplicate toasts
        autoClose: 5000,
      })
    }

    return Promise.reject(error)
  }
)

export default apiClient
